import React, { forwardRef, useMemo } from 'react';
import JoditEditor, { Jodit } from 'jodit-react';
import PropTypes from 'prop-types';
import { getLocalStorageItem } from '../../../utils';
import { LOCAL_STORAGE } from '../../../constants';

const TextEditor = forwardRef(
    (
        {
            content = '',
            readonly = false,
            toolbarSticky = false,
            toolbarAdaptive = false,
            toolbarButtonSize = 'large',
            onBlur = () => {},
            onChange = () => {},
            height = 640,
            ...props
        },
        ref,
    ) => {
        const theme = getLocalStorageItem(LOCAL_STORAGE.DARK_MODE) === 'true' ? 'dark' : 'light';

        Jodit.modules.Icon.set(
            'removeformat',
            `<svg width="18" height="18" viewBox="0 0 24 24" data-name="Line Color" xmlns="http://www.w3.org/2000/svg" class="icon line-color"><rect x="9" y="3" width="6" height="3" rx="1" style="fill:none;stroke:var(--jd-color-icon);stroke-linecap:round;stroke-linejoin:round;stroke-width:2"/><path data-name="secondary" style="fill:none;stroke:var(--jd-color-icon);stroke-linecap:round;stroke-linejoin:round;stroke-width:2" d="m10 15 4-4m-4 0 4 4"/><path d="M18 4a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1" style="fill:none;stroke:var(--jd-color-icon);stroke-linecap:round;stroke-linejoin:round;stroke-width:2"/></svg>`,
        );

        const config = useMemo(() => {
            return {
                readonly,
                height,
                padding: 10,
                toolbarSticky,
                toolbarAdaptive,
                toolbarButtonSize,
                theme,
                buttons: [
                    'undo',
                    'redo',
                    '|',
                    'bold',
                    'italic',
                    'underline',
                    'strikethrough',
                    '|',
                    'ul',
                    'ol',
                    '|',
                    'outdent',
                    'indent',
                    '|',
                    'font',
                    'fontsize',
                    'brush',
                    'paragraph',
                    '|',
                    'table',
                    'hr',
                    // 'symbol',
                    // 'emoji',
                    '|',
                    'left',
                    'center',
                    'right',
                    'justify',
                    '|',
                    'copyformat',
                    {
                        name: 'removeformat',
                        tooltip: 'Remove format',
                    },
                    'link',
                    // FOR FUTURE USE: Uncomment to enable file upload
                    // {
                    //     name: 'image',
                    //     tooltip: 'Upload File',
                    //     exec: () => {
                    //         const input = document.createElement('input');
                    //         input.type = 'file';
                    //         input.accept = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt'; // allow multiple types
                    //         input.click();

                    //         input.onchange = async () => {
                    //             const file = input.files[0];
                    //             if (!file) return;
                    //             console.log("file", file);

                    //             // Upload the file using your pre-signed URL logic
                    //         };
                    //     },
                    // },
                    '|',
                    'selectall',
                    'cut',
                    'copy',
                    'paste',
                    '|',
                    'preview',
                    'print',
                    'fullsize',
                    // 'source',
                ],
                askBeforePasteHTML: false,
                askBeforePasteFromWord: false,
                defaultActionOnPaste: 'insert_clear_html',
                showXPathInStatusbar: false,
                spellcheck: true,
                showPoweredByJodit: false,
                uploader: {
                    insertImageAsBase64URI: false, //  we want S3 URL so false
                    url: '', // Disable default upload
                    isSuccess: (resp) => resp && resp.publicUrl,
                    process: (resp) => ({ files: [resp.publicUrl] }), // expected default format by Jodit
                    defaultHandlerSuccess: false,
                },
                ...props,
            };
        }, [height, props, readonly, theme, toolbarAdaptive, toolbarButtonSize, toolbarSticky]);

        return (
            <JoditEditor
                ref={ref}
                value={content}
                config={config}
                onBlur={onBlur}
                onChange={onChange}
                contentClassName='jodit-custom-content'
            />
        );
    },
);

TextEditor.propTypes = {
    content: PropTypes.string.isRequired,
    readonly: PropTypes.bool,
    toolbarSticky: PropTypes.bool,
    toolbarAdaptive: PropTypes.bool,
    toolbarButtonSize: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    ref: PropTypes.object.isRequired,
};

TextEditor.displayName = 'TextEditor';

export default TextEditor;
